import { useQueryClient } from '@tanstack/react-query';
import { useGoodsSno } from './useGoodsSno';
import { useCallback, useEffect } from 'react';
import { queryKey } from '../constant/queryKey';
import { queryKey as sharedQueryKey } from 'shared/src/constant/queryKey';
import {
  AtomicItemListType,
  goodsService,
  type ScreensReadModel,
  screensService,
} from 'shared/src/api';
import { isNotNil } from 'shared/src/utils';
import { useInstanceId, useSafeTimeout } from 'shared/src/hooks';
import { useFixedCouponSno } from './useFixedCouponSno';
import { useSameMarketRecommendOnly } from './useSameMarketRecommendOnly';
import { isSameMarketProxyComponent } from '../utils/isSameMarketProxyComponent';
import { useConfigs } from 'shared/src/queries';

export const usePreloadQuery = () => {
  const client = useQueryClient();
  const goodsSno = useGoodsSno();
  const setSafeTimeout = useSafeTimeout();
  const fixedCouponSno = useFixedCouponSno();
  const sameMarketRecommendOnly = useSameMarketRecommendOnly();
  const instanceId = useInstanceId();

  useConfigs();

  // 첫 페인트(헤더/가격/커버/첫 섹션)에 실제로 필요한 쿼리만 즉시 prefetch한다.
  // 할인/쿠폰/중간광고는 첫 페인트를 막지 않으므로 secondary 로 미뤄, 무거운 콘텐츠
  // 마운트 commit 과 동시에 터지던 요청 burst(요청별 헤더 인터셉터 churn)를 줄인다.
  const prefetchCritical = useCallback(() => {
    client.prefetchQuery({
      queryKey: queryKey.goodsBasic(goodsSno),
      queryFn: () => goodsService.getGoodsBasic(goodsSno),
    });
    client.prefetchQuery({
      queryKey: queryKey.goodsDetail(goodsSno),
      queryFn: () => goodsService.getGoodsDetail(goodsSno),
    });
    // 기획전 이벤트 연관상품 off 실험: 타 마켓 연관 추천 광고는 prefetch도 하지 않는다.
    if (!sameMarketRecommendOnly) {
      client.prefetchQuery({
        queryKey: queryKey.goodsMidRelatedRecommend(goodsSno),
        queryFn: () => goodsService.getMidRelatedAdGoods(goodsSno),
      });
    }

    client.prefetchQuery({
      queryKey: queryKey.goodsCodi(goodsSno),
      queryFn: () => goodsService.getCodiGoods(goodsSno),
    });

    client
      .prefetchQuery({
        queryKey: queryKey.goodsAtomicComponents(goodsSno),
        queryFn: () =>
          screensService.get({
            screen: 'GOODS_DETAIL',
            goods_sno: goodsSno,
            next_token: null,
          }),
      })
      .then(() => {
        const atomicComponents = client.getQueryData<ScreensReadModel>(
          queryKey.goodsAtomicComponents(goodsSno),
        );
        if (isNotNil(atomicComponents)) {
          const proxyComponents = atomicComponents.components.filter(
            component =>
              component.type.itemList === AtomicItemListType.Proxy &&
              // 기획전 이벤트 연관상품 off 실험: 해당 마켓 상품(176) 외 proxy 는 prefetch도 하지 않는다.
              (!sameMarketRecommendOnly ||
                isSameMarketProxyComponent(component)),
          );
          proxyComponents.map(component => {
            const requestUrl = component.entity.body.item.url;
            return client.prefetchQuery({
              queryKey: [
                ...sharedQueryKey.proxy(instanceId, requestUrl),
                { ably_only_log: {}, analytics: {} },
              ],
              queryFn: () =>
                screensService.getProxyComponents({ url: requestUrl }),
            });
          });
        }
      });
  }, [client, goodsSno, instanceId, sameMarketRecommendOnly]);

  const prefetchSecondary = useCallback(() => {
    client.prefetchQuery({
      queryKey: queryKey.goodsDiscount(goodsSno, fixedCouponSno),
      queryFn: () => goodsService.getGoodsDiscounts(goodsSno, fixedCouponSno),
    });
    client.prefetchQuery({
      queryKey: queryKey.goodsCoupon(goodsSno, fixedCouponSno),
      queryFn: () => goodsService.getGoodsCoupon(goodsSno, fixedCouponSno),
    });
    // VIEW_GOODS_DETAIL 게이트 대상. 프리로드하지 않으면 content 마운트(runAfterInteractions)
    // 이후에야 요청이 시작돼, review 응답을 기다리는 동안 이탈하면 VIEW 가 누락됐다.
    client.prefetchQuery({
      queryKey: queryKey.goodsReviewSummary(goodsSno),
      queryFn: () => goodsService.getGoodsReviewSummary(goodsSno),
    });
    client.prefetchQuery({
      queryKey: queryKey.goodsOptionsBrief(goodsSno),
      queryFn: () => goodsService.getGoodsOptionsBrief(goodsSno),
    });
    client.prefetchQuery({
      queryKey: queryKey.goodsOptions(goodsSno, { depth: 1 }),
      queryFn: () => goodsService.getGoodsOptions(goodsSno, { depth: 1 }),
    });
    // 기획전 이벤트 연관상품 off 실험: 커버 연관(유사) 상품은 prefetch도 하지 않는다.
    if (!sameMarketRecommendOnly) {
      client.prefetchQuery({
        queryKey: queryKey.similarGoods(goodsSno),
        queryFn: () => goodsService.getSimilarGoods(goodsSno),
      });
    }
  }, [client, fixedCouponSno, goodsSno, sameMarketRecommendOnly]);

  useEffect(() => {
    prefetchCritical();
    setSafeTimeout(prefetchSecondary, 0);
  }, [prefetchCritical, prefetchSecondary, setSafeTimeout]);
};
